/**
 * CameraConfig.js - Centralized camera configuration and constants
 * All camera positions, POI limits, and default settings in one place
 */

import * as THREE from 'three'

// ============================================================================
// DEFAULT CAMERA POSITIONS FOR EACH SCENE
// ============================================================================

export const DEFAULT_CAMERA_POSITIONS = {
  scene1: {
    landscape: {
      position: new THREE.Vector3(0, 6, 55),
      target: new THREE.Vector3(-5, 6, -5)
    },
    portrait: {
      position: new THREE.Vector3(0, 8, 55),
      target: new THREE.Vector3(-3, 6, -5)
    }
  },
  scene2: {
    landscape: {
      position: new THREE.Vector3(-15, 10, 35),
      target: new THREE.Vector3(0, 10, 0)
    },
    portrait: {
      position: new THREE.Vector3(-15, 12, 30),
      target: new THREE.Vector3(0, 10, 0)
    }
  }
}

/**
 * Get default camera position for a scene based on orientation
 * @param {string} scene - 'scene1' or 'scene2'
 * @param {boolean} isLandscape - True for landscape orientation, false for portrait
 * @returns {Object} {position: Vector3, target: Vector3}
 */
export function getDefaultCameraPosition(scene, isLandscape = true) {
  const orientation = isLandscape ? 'landscape' : 'portrait'
  return DEFAULT_CAMERA_POSITIONS[scene][orientation]
}

// ============================================================================
// GANTRY TRANSFORMATIONS (for calculating launch platform POI positions)
// ============================================================================

export const GANTRY_TRANSFORM = {
  position: [0, -1.31, 0],
  rotation: [0, Math.PI / 0.765, 0]
}

// ============================================================================
// ROCKET TRANSFORMATIONS (for calculating Black Arrow POI positions)
// ============================================================================

export const ROCKET_TRANSFORM = {
  position: [0, 10, 0],
  rotation: [0, Math.PI / 1.675, 0]
}

// ============================================================================
// LOCAL POI POSITIONS (relative to parent groups)
// ============================================================================

export const LOCAL_POI_POSITIONS = {
  // Clamp Assembly POIs - Landscape (default)
  clamp: {
    POI: new THREE.Vector3(1.261, 1.681, -1.247),
    POS: new THREE.Vector3(9.373, 4.719, -4.965),
    TAR: new THREE.Vector3(-4, 1.395, 0.198)
  },
  // Clamp Assembly POIs - Portrait
  clampPortrait: {
    POI: new THREE.Vector3(1.261, 1.681, -1.247),
    POS: new THREE.Vector3(9.373, 5.5, -4.965),
    TAR: new THREE.Vector3(0, 1, 0)
  },
  // Fuel System POIs (relative to Fuel mesh at [-3, 1.055, -3]) - Landscape (default)
  fuel: {
    POI: new THREE.Vector3(-3 + 0.189, 1.055 + 6.969, -3 + (-0.409)),
    POS: new THREE.Vector3(7, 1.055 + 12.331, -3 + (-7.84)),
    TAR: new THREE.Vector3(-0.6, 9, -0.9)
  },
  // Fuel System POIs - Portrait
  fuelPortrait: {
    POI: new THREE.Vector3(-3 + 0.189, 1.055 + 6.969, -3 + (-0.409)),
    POS: new THREE.Vector3(7, 1.055 + 13.5, -3 + (-7.84)),
    TAR: new THREE.Vector3(-0.6, 9, -0.9)
  },
  // Control Shack POIs (relative to Shack group at [-0.75, 0, 24.125]) - Landscape (default)
  shack: {
    POI: new THREE.Vector3(-0.75 + (-1.359), 0 + 5.712, 24.125 + (-1.625)),
    POS: new THREE.Vector3(-0.75 + 23.753, 0 + 19.023, 24.125 + (-28.085)),
    TAR: new THREE.Vector3(-0.75 + (-2.938), 0 + 9.016, 24.125 + (-1.924))
  },
  // Control Shack POIs - Portrait
  shackPortrait: {
    POI: new THREE.Vector3(-0.75 + (-1.359), 0 + 5.712, 24.125 + (-1.625)),
    POS: new THREE.Vector3(23.753, 12, 4),
    TAR: new THREE.Vector3(2, 6, 22)
  }
}

// ============================================================================
// BLACK ARROW WORLD POI POSITIONS (actual world positions from scene)
// ============================================================================

export const BLACK_ARROW_WORLD_POI_POSITIONS = {
  // Stage 2 (ST2) - Landscape
  st2: {
    POI: new THREE.Vector3(1.571, 10.188, 0.643),
    POS: new THREE.Vector3(-5.538, 11.413, 13.531),
    TAR: new THREE.Vector3(0.000, 11.413, 0.000)
  },
  // Stage 2 (ST2) - Portrait
  st2Portrait: {
    POI: new THREE.Vector3(1.571, 10.188, 0.643),
    POS: new THREE.Vector3(-5.538, 10, 13.531),
    TAR: new THREE.Vector3(0.000, 10, 0.000)
  },
  // Stage 1 (ST1) - Landscape
  st1: {
    POI: new THREE.Vector3(1.571, 2.958, 0.643),
    POS: new THREE.Vector3(-7.890, 3.499, 12),
    TAR: new THREE.Vector3(0.000, 3.499, 0.000)
  },
  // Stage 1 (ST1) - Portrait
  st1Portrait: {
    POI: new THREE.Vector3(1.571, 2.958, 0.643),
    POS: new THREE.Vector3(-7.890, 2.0, 10),
    TAR: new THREE.Vector3(0.000, 2.0, 0.000)
  },
  // Stage 3 (ST3) - Landscape
  st3: {
    POI: new THREE.Vector3(1.571, 16.446, 0.643),
    POS: new THREE.Vector3(-3.030, 16.446, 4),
    TAR: new THREE.Vector3(0.000, 16.446, 0.000)
  },
  // Stage 3 (ST3) - Portrait
  st3Portrait: {
    POI: new THREE.Vector3(1.571, 16.446, 0.643),
    POS: new THREE.Vector3(-3.030, 16, 2),
    TAR: new THREE.Vector3(0.000, 16, 0.000)
  },
  // Prospero (PRO) - Landscape orientation (default)
  pro: {
    POI: new THREE.Vector3(1.571, 19.761, 0.643),
    POS: new THREE.Vector3(-3.029, 20.907, 7.402),
    TAR: new THREE.Vector3(0.000, 20.907, 0.000)
  },
  // Prospero (PRO) - Portrait orientation (alternative camera angle)
  proPortrait: {
    POI: new THREE.Vector3(1.571, 19.761, 0.643),
    POS: new THREE.Vector3(-3.029, 19.25, 4),
    TAR: new THREE.Vector3(0.000, 19.25, 0.000)
  }
}

// ============================================================================
// LAUNCH PLATFORM (SCENE 1) POI CAMERA LIMITS
// ============================================================================

export const LAUNCH_PLATFORM_POI_LIMITS = {
  'POI_LP_Clamp': {
    minAzimuthAngle: -Math.PI / 8,    // -60° L
    maxAzimuthAngle: -Math.PI / 18,     // +60° R (higher is less)
    minPolarAngle: Math.PI / 2.5,       // 60° from vertical
    maxPolarAngle: Math.PI / 2,   // 120° from vertical
    minDistance: 5,
    maxDistance: 25,
    description: 'Clamp Assembly - Restricted rotation for detail viewing'
  },
  'POI_LP_Fuel': {
    minAzimuthAngle: -Math.PI / 12,    // -60° L
    maxAzimuthAngle: Math.PI / 8,     // +60° R (higher is less)
    minPolarAngle:  Math.PI / 3,       // 45° from vertical
    maxPolarAngle: 2 * Math.PI / 4,   // 135° from vertical
    minDistance: 5,
    maxDistance: 30,
    description: 'Fuel Systems - Moderate rotation limits'
  },
  'POI_LP_Shack': {
    minAzimuthAngle: -Math.PI / 24,    // -60° L
    maxAzimuthAngle: Math.PI / 8,     // +60° R (higher is less)
    minPolarAngle: Math.PI / 3,       // 60° from vertical
    maxPolarAngle: 2 * Math.PI / 4,   // 120° from vertical
    minDistance: 5,
    maxDistance: 40,
    description: 'Control Shack - Wide viewing angles'
  }
}

// ============================================================================
// LAUNCH PLATFORM (SCENE 1) DEFAULT CAMERA LIMITS
// ============================================================================

export const LAUNCH_PLATFORM_CAMERA_LIMITS = {
  minAzimuthAngle: -Math.PI / 32,    // Default: ~-5.6°
  maxAzimuthAngle: Math.PI / 16,     // Default: ~11.25°
  minPolarAngle: Math.PI / 2.15,     // Default: ~66.8° from vertical
  maxPolarAngle: Math.PI / 2,        // Default: 90° (horizontal)
  minDistance: 5,
  maxDistance: 50,
  description: 'Launch Platform - General viewing limits'
}

// ============================================================================
// BLACK ARROW (SCENE 2) POI CAMERA LIMITS
// ============================================================================

export const BLACK_ARROW_POI_LIMITS = {
  'POI_BA_ST2': {
    // Stage 2 - Main engine section - allow full 360° rotation
    minAzimuthAngle: -Math.PI / 3,    // -90°
    maxAzimuthAngle: Math.PI / 7,     // +90°
    minPolarAngle: 1,              // Near vertical
    maxPolarAngle: Math.PI - 1,    // Near vertical
    minDistance: 0.1,
    maxDistance: 8,
    description: 'Stage 2 - Full 360° rotation'
  },
  'POI_BA_ST1': {
    // Stage 1 - Upper stage - restricted rotation for detail viewing
    minAzimuthAngle: -Math.PI / 2,    // -90°
    maxAzimuthAngle: Math.PI / 7,     // +90°
    minPolarAngle: 1,              // Near vertical
    maxPolarAngle: Math.PI - 1,    // Near vertical
    minDistance: 0.5,
    maxDistance: 6,
    description: 'Stage 1 - Limited rotation for detail focus'
  },
  'POI_BA_ST3': {
    // Stage 3 - Nose cone - very restricted for precision viewing
    minAzimuthAngle: -Math.PI / 3,    // -90°
    maxAzimuthAngle: Math.PI / 7,     // +90°
    minPolarAngle: 1,              // Near vertical
    maxPolarAngle: Math.PI - 1,    // Near vertical
    minDistance: 0.2,
    maxDistance: 4,
    description: 'Stage 3 - Tight constraints for nose cone detail'
  },
  'POI_BA_PRO': {
    // Propulsion - wide view for engine cluster
    minAzimuthAngle: -Math.PI / 3,    // -90°
    maxAzimuthAngle: Math.PI / 7,     // +90°
    minPolarAngle: 1,              // Near vertical
    maxPolarAngle: Math.PI - 1,    // Near vertical
    minDistance: 0.3,
    maxDistance: 10,
    description: 'Propulsion - Wide view for engine cluster'
  }
}

// ============================================================================
// GANTRY TRANSFORMATIONS (for calculating launch platform POI positions)
// ============================================================================

export const CAMERA_CONTROLS_DEFAULTS = {
  minDistance: 5,
  maxDistance: 50,
  enableZoom: false,
  enablePan: false,
  smoothTime: 0.1,
  // Scene 1 default limits
  scene1: {
    maxPolarAngle: Math.PI / 2,
    minPolarAngle: Math.PI / 2.15,
    maxAzimuthAngle: Math.PI / 16,
    minAzimuthAngle: -Math.PI / 32
  },
  // Scene 2 default limits (zoomed out)
  scene2Default: {
    maxPolarAngle: Math.PI - 0.01,
    minPolarAngle: 0.01,
    maxAzimuthAngle: Math.PI * 2,
    minAzimuthAngle: -Math.PI * 2
  }
}

// ============================================================================
// ANIMATION TIMINGS
// ============================================================================

export const ANIMATION_TIMINGS = {
  cameraMovement: 2000,      // ms - standard camera movement duration
  poiZoomDelay: 1000,        // ms - delay before applying POI limits
  transitionDuration: 6000,  // ms - total launch sequence duration
  orientationChange: 0       // ms - immediate camera update when orientation changes
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get POI limits for a specific Black Arrow POI
 */
export function getPOILimits(poiName) {
  return BLACK_ARROW_POI_LIMITS[poiName] || null
}

/**
 * Get launch platform POI limits by POI name
 */
export function getLaunchPlatformPOILimits(poiName) {
  return LAUNCH_PLATFORM_POI_LIMITS[poiName] || null
}

/**
 * Get Black Arrow world positions (no transformation needed, already in world space)
 * @param {boolean} isLandscape - Whether the orientation is landscape
 * @returns {Object} World positions for all POI/POS/TAR positions
 */
export function getBlackArrowWorldPositions(isLandscape = false) {
  const worldPositions = {}

  // Define which positions to use based on orientation
  const positionKeys = isLandscape 
    ? ['st2', 'st1', 'st3', 'pro']
    : ['st2Portrait', 'st1Portrait', 'st3Portrait', 'proPortrait']

  // Map to base keys for output
  const baseKeys = ['st2', 'st1', 'st3', 'pro']

  positionKeys.forEach((key, index) => {
    const baseKey = baseKeys[index]
    const positions = BLACK_ARROW_WORLD_POI_POSITIONS[key]
    
    if (positions) {
      Object.entries(positions).forEach(([type, worldPos]) => {
        worldPositions[`${baseKey}_${type}`] = worldPos
      })
    }
  })

  return worldPositions
}
